import { WorkOrder } from "@/services/backend/vbs/work-orders/types/work-order";
import {
  useVbsTreatmentShowQuery,
  useVbsTreatmentUpdateCommentMutation,
  useVbsTreatmentUpdateComponentMutation,
} from "@/services/backend/vbs/treatments/api/treatment-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Position,
  positionTitle,
} from "@/services/backend/vbs/treatments/types/position";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  RefreshCw,
  Trash,
} from "lucide-react";
import { CompletedCell } from "@/services/backend/vbs/treatments/components/completed-cell";
import { Button } from "@/shared/components/ui/button";
import {
  useVbsDocumentationDeleteImageMutation,
  useVbsDocumentationShowImageQuery,
  useVbsDocumentationShowQuery,
  useVbsDocumentationUploadImageForPositionMutation,
} from "@/services/backend/vbs/work-orders/api/documentation-api";
import { useToast } from "@/shared/hooks/use-toast";
import t from "@/lang/lang";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Label } from "@/shared/components/ui/label";
import { cn } from "@/shared/lib/utils";
import { Input } from "@/shared/components/ui/input";
import imageCompression from "browser-image-compression";
import { v4 } from "uuid";
import {
  Image,
  Item,
} from "@/services/backend/vbs/work-orders/types/documentation";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/shared/components/ui/carousel";
import { uniqueAreas } from "@/services/backend/vbs/treatments/types/treatment";
import {
  AccessPanel,
  AerosolSeparator,
  AluminiumMeshGreaseFilter,
  Anemostat,
  Component,
  DummyPlate,
  ExhaustDuctAngular,
  ExhaustDuctRound,
  ExhaustHood,
  ExhaustHoodSystem,
  ExtraHours,
  Kind,
  Lighting,
  PassiveArea,
  SupplyDuctAngular,
  SupplyDuctRound,
  VentilatedCeiling,
  VentilatedCeilingSystem,
} from "@/services/backend/vbs/treatments/types/component";
import { EXECUTION_AREA_KEYS } from "@/routes/processes/[processId]/vbs/work-orders/execution/[workOrderId]/treatment/_components/add-extra-work-during-execution-drop-down";
import { DeletePositionDialog } from "@/services/backend/vbs/treatments/components/delete-position-dialog";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { NumberInput } from "@/shared/components/form/number-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { TextareaInput } from "@/shared/components/form/textarea-input";
import {
  ConnectionAlert,
  useConnectionQuality,
} from "@/shared/components/alerts/connection-alert";
import { Spinner } from "@/shared/components/ui/spinner";

export function PositionsByArea({ workOrder }: { workOrder: WorkOrder }) {
  const {
    data: treatment,
    isLoading,
    error,
  } = useVbsTreatmentShowQuery({ id: workOrder.treatmentId });

  if (isLoading) {
    return <div />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const areas = uniqueAreas(treatment!);

  return (
    <div className="grid grid-cols-1 gap-2">
      <ConnectionAlert
        options={{ pingEndpoint: `${import.meta.env.VITE_BACKEND_URL}/ping` }}
      />
      {areas.map((area) => (
        <AreaCard
          area={area}
          positions={treatment!.positions.filter((p) => p.area === area)}
          workOrder={workOrder}
          key={area}
        />
      ))}
    </div>
  );
}

function AreaCard({
  area,
  positions,
  workOrder,
}: {
  area: string;
  positions: Position[];
  workOrder: WorkOrder;
}) {
  const [open, setOpen] = useState(true);

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="cursor-pointer">
          <CardHeader>
            <CardTitle>
              <span className="flex items-center space-x-2">
                {open ? <ChevronDown /> : <ChevronRight />}
                <AllPositionsCompletedIndicator positions={positions} />
                <span>{area}</span>
              </span>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent>
            <div className="divide-y border-t border-b">
              {positions.map((position) => (
                <PositionLine
                  workOrder={workOrder}
                  position={position}
                  key={position.id}
                />
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function AllPositionsCompletedIndicator({
  positions,
}: {
  positions: Position[];
}) {
  const allPositionsCompleted = positions.every((p) => p.completed);

  if (!allPositionsCompleted) {
    return null;
  }

  return <Check className="text-green-700" />;
}

function PositionLine({
  workOrder,
  position,
}: {
  workOrder: WorkOrder;
  position: Position;
}) {
  const { isPoor, isOffline } = useConnectionQuality();

  return (
    <div className="hover:bg-accent">
      <div className="flex items-center justify-between px-2 py-2">
        <div className="flex items-center space-x-4">
          <CompletedCell
            treatmentId={workOrder.treatmentId}
            position={position}
            disabled={isPoor || isOffline}
          />
          <PositionLineTitle position={position} />
        </div>
        <div className="flex items-center gap-2">
          {EXECUTION_AREA_KEYS.includes(position.area) && (
            <DeletePositionDialog
              treatmentId={workOrder.treatmentId}
              position={position}
              disabled={isPoor || isOffline}
            />
          )}
          <PhotoDocumentationButtons
            workOrder={workOrder}
            position={position}
            disabled={isPoor || isOffline}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 pr-2 pb-2">
        <div>
          <PositionDetails
            treatmentId={workOrder.treatmentId}
            position={position}
          />
          <PositionCycle position={position} />
        </div>
        <PositionComment
          treatmentId={workOrder.treatmentId}
          position={position}
          disabled={isPoor || isOffline}
        />
      </div>
    </div>
  );
}

function PositionLineTitle({ position }: { position: Position }) {
  const title = positionTitle(position);

  return (
    <div className="flex text-xl font-semibold tracking-tight">
      <span className="w-12">{position.number}</span>
      <span>{title}</span>
    </div>
  );
}

function PositionCycle({ position }: { position: Position }) {
  if (position.cycle === 0) {
    return null;
  }

  return (
    <div className="pb-2 pl-26 text-sm">
      {t("Turnus:")} {position.cycle} {t("wöchentlich")}
    </div>
  );
}

function PositionComment({
  treatmentId,
  position,
  disabled,
}: {
  treatmentId: string;
  position: Position;
  disabled?: boolean;
}) {
  const [commentReq, setCommentReq] = useState({
    id: treatmentId,
    positionId: position.id,
    comment: position.comment,
  });
  const mutation = useVbsTreatmentUpdateCommentMutation();
  const { error: cError } = useDebouncedMutation(
    commentReq,
    setCommentReq,
    mutation,
    {
      id: treatmentId,
      positionId: position.id,
      comment: position.comment,
    },
  );

  return (
    <TextareaInput
      label={t("Kommentar")}
      field="comment"
      text={commentReq.comment}
      onTextChange={(comment) => setCommentReq({ ...commentReq, comment })}
      errors={inputValidationErrorMessages(cError, "comment")}
      textareaClassName="min-h-0"
      disabled={disabled}
    />
  );
}

function PositionDetails({
  treatmentId,
  position,
}: {
  treatmentId: string;
  position: Position;
}) {
  if (EXECUTION_AREA_KEYS.includes(position.area)) {
    return (
      <EditablePositionDetails treatmentId={treatmentId} position={position} />
    );
  }

  return <ImmutablePositionDetails position={position} />;
}

function EditablePositionDetails({
  treatmentId,
  position,
}: {
  treatmentId: string;
  position: Position;
}) {
  switch (position.component.kind) {
    case Kind.ExtraHours:
      return <ExtraHoursInput treatmentId={treatmentId} position={position} />;
    case Kind.AccessPanel:
      return <AccessPanelInput treatmentId={treatmentId} position={position} />;
    default:
      return <ImmutablePositionDetails position={position} />;
  }
}

function ExtraHoursInput({
  treatmentId,
  position,
}: {
  treatmentId: string;
  position: Position;
}) {
  const [request, setRequest] = useState({
    id: treatmentId,
    positionId: position.id,
    component: position.component,
  });
  const data = request.component.data as ExtraHours;
  const mutation = useVbsTreatmentUpdateComponentMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: treatmentId,
    positionId: position.id,
    component: position.component,
  });

  return (
    <div className="w-1/2 pr-2 pb-2 pl-26">
      <NumberInput
        label={t("Stunden")}
        field="hours"
        number={data.hours}
        onNumberChange={(hours) =>
          setRequest({
            ...request,
            component: { ...request.component, data: { ...data, hours } },
          })
        }
        errors={inputValidationErrorMessages(error, "component.data.hours")}
      />
    </div>
  );
}

function AccessPanelInput({
  treatmentId,
  position,
}: {
  treatmentId: string;
  position: Position;
}) {
  const [request, setRequest] = useState({
    id: treatmentId,
    positionId: position.id,
    component: position.component,
  });
  const data = request.component.data as AccessPanel;
  const mutation = useVbsTreatmentUpdateComponentMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: treatmentId,
    positionId: position.id,
    component: position.component,
  });

  return (
    <div className="w-1/2 gap-4 pr-2 pb-2 pl-26">
      <NumberInput
        label={t("Stück")}
        field="hours"
        number={data.count}
        onNumberChange={(count) =>
          setRequest({
            ...request,
            component: { ...request.component, data: { ...data, count } },
          })
        }
        errors={inputValidationErrorMessages(error, "component.data.count")}
      />
    </div>
  );
}

function ImmutablePositionDetails({ position }: { position: Position }) {
  return (
    <div className="flex gap-4 pr-2 pb-2 pl-26 text-xs">
      <div>{quantity(position.component)}</div>
      <div>{componentString(position.component)}</div>
      <div>{measures(position.component)}</div>
    </div>
  );
}

function quantity(component: Component): ReactNode {
  const { kind, data } = component;

  switch (kind) {
    case Kind.AccessPanel:
      return `${(data as AccessPanel).count} Stk`;
    case Kind.AerosolSeparator:
      return `${(data as AerosolSeparator).count} Stk`;
    case Kind.AirHandlingUnit:
      return `1 Stk`;
    case Kind.AluminiumMeshGreaseFilter:
      return `${(data as AluminiumMeshGreaseFilter).count} Stk`;
    case Kind.Anemostat:
      return `${(data as Anemostat).count} Stk`;
    case Kind.DummyPlate:
      return `${(data as DummyPlate).count} Stk`;
    case Kind.ExhaustDuctAngular:
      return `${(data as ExhaustDuctAngular).length} m`;
    case Kind.ExhaustDuctRound:
      return `${(data as ExhaustDuctRound).length} m`;
    case Kind.ExhaustHood:
      return "1 Stk";
    case Kind.ExhaustHoodSystem:
      // eslint-disable-next-line no-case-declarations
      const system = data as ExhaustHoodSystem;
      return (
        <div>
          {(system.exhaustHood.data as ExhaustHood).length > 0 && (
            <div>{quantity(system.exhaustHood)}</div>
          )}
          {(system.aerosolSeparator.data as AerosolSeparator).count > 0 && (
            <div>{quantity(system.aerosolSeparator)}</div>
          )}
          {(system.aluminiumMeshGreaseFilter.data as AluminiumMeshGreaseFilter)
            .count > 0 && (
            <div>{quantity(system.aluminiumMeshGreaseFilter)}</div>
          )}
          {(system.dummyPlate.data as DummyPlate).count > 0 && (
            <div>{quantity(system.dummyPlate)}</div>
          )}
          {(system.lighting.data as Lighting).count > 0 && (
            <div>{quantity(system.lighting)}</div>
          )}
        </div>
      );
    case Kind.ExtraHours:
      return `${(data as ExtraHours).hours} Std.`;
    case Kind.FreeText:
      return "";
    case Kind.Lighting:
      return `${(data as Lighting).count} Stk`;
    case Kind.PassiveArea:
      return "1 Stk";
    case Kind.SupplyDuctAngular:
      return `${(data as SupplyDuctAngular).length} m`;
    case Kind.SupplyDuctRound:
      return `${(data as SupplyDuctRound).length} m`;
    case Kind.VentilatedCeiling:
      return "1 Stk";
    case Kind.VentilatedCeilingSystem:
      // eslint-disable-next-line no-case-declarations
      const vcsystem = data as VentilatedCeilingSystem;
      return (
        <div>
          {(vcsystem.ventilatedCeiling.data as VentilatedCeiling).length >
            0 && <div>{quantity(vcsystem.ventilatedCeiling)}</div>}
          {(vcsystem.aerosolSeparator.data as AerosolSeparator).count > 0 && (
            <div>{quantity(vcsystem.aerosolSeparator)}</div>
          )}
          {(vcsystem.dummyPlate.data as DummyPlate).count > 0 && (
            <div>{quantity(vcsystem.dummyPlate)}</div>
          )}
          {(vcsystem.lighting.data as Lighting).count > 0 && (
            <div>{quantity(vcsystem.lighting)}</div>
          )}
        </div>
      );
    default:
      return "";
  }
}

function componentString(component: Component): ReactNode {
  const { kind } = component;

  switch (kind) {
    case Kind.ExhaustHoodSystem:
      // eslint-disable-next-line no-case-declarations
      const ehs = component.data as ExhaustHoodSystem;
      return (
        <div>
          {(ehs.exhaustHood.data as ExhaustHood).length > 0 && (
            <div>{componentString(ehs.exhaustHood)}</div>
          )}
          {(ehs.aerosolSeparator.data as AerosolSeparator).count > 0 && (
            <div>{componentString(ehs.aerosolSeparator)}</div>
          )}
          {(ehs.aluminiumMeshGreaseFilter.data as AluminiumMeshGreaseFilter)
            .count > 0 && (
            <div>{componentString(ehs.aluminiumMeshGreaseFilter)}</div>
          )}
          {(ehs.dummyPlate.data as DummyPlate).count > 0 && (
            <div>{componentString(ehs.dummyPlate)}</div>
          )}
          {(ehs.lighting.data as Lighting).count > 0 && (
            <div>{componentString(ehs.lighting)}</div>
          )}
        </div>
      );
    case Kind.VentilatedCeilingSystem:
      // eslint-disable-next-line no-case-declarations
      const vcs = component.data as VentilatedCeilingSystem;
      return (
        <div>
          {(vcs.ventilatedCeiling.data as VentilatedCeiling).length > 0 && (
            <div>{componentString(vcs.ventilatedCeiling)}</div>
          )}
          {(vcs.aerosolSeparator.data as AerosolSeparator).count > 0 && (
            <div>{componentString(vcs.aerosolSeparator)}</div>
          )}
          {(vcs.dummyPlate.data as DummyPlate).count > 0 && (
            <div>{componentString(vcs.dummyPlate)}</div>
          )}
          {(vcs.lighting.data as Lighting).count > 0 && (
            <div>{componentString(vcs.lighting)}</div>
          )}
        </div>
      );
    case Kind.FreeText:
      return "";
    default:
      return t(kind);
  }
}

function measures(component: Component): ReactNode {
  const { kind, data } = component;

  switch (kind) {
    case Kind.AccessPanel:
      return "";
    case Kind.AerosolSeparator:
      return "";
    case Kind.AirHandlingUnit:
      return "";
    case Kind.AluminiumMeshGreaseFilter:
      return "";
    case Kind.Anemostat:
      return "";
    case Kind.DummyPlate:
      return "";
    case Kind.ExhaustDuctAngular:
      // eslint-disable-next-line no-case-declarations
      const eda = data as ExhaustDuctAngular;
      return `${eda.width} x ${eda.height} mm`;
    case Kind.ExhaustDuctRound:
      // eslint-disable-next-line no-case-declarations
      const edr = data as ExhaustDuctRound;
      return `ø ${edr.diameter} mm`;
    case Kind.ExhaustHood:
      // eslint-disable-next-line no-case-declarations
      const eh = data as ExhaustHood;
      return `${eh.length} x ${eh.width} m`;
    case Kind.ExhaustHoodSystem:
      // eslint-disable-next-line no-case-declarations
      const ehs = data as ExhaustHoodSystem;
      return (
        <div>
          {(ehs.exhaustHood.data as ExhaustHood).length > 0 && (
            <div>{measures(ehs.exhaustHood)}</div>
          )}
          {(ehs.aerosolSeparator.data as AerosolSeparator).count > 0 && (
            <div>{measures(ehs.aerosolSeparator)}</div>
          )}
          {(ehs.aluminiumMeshGreaseFilter.data as AluminiumMeshGreaseFilter)
            .count > 0 && <div>{measures(ehs.aluminiumMeshGreaseFilter)}</div>}
          {(ehs.dummyPlate.data as DummyPlate).count > 0 && (
            <div>{measures(ehs.dummyPlate)}</div>
          )}
          {(ehs.lighting.data as Lighting).count > 0 && (
            <div>{measures(ehs.lighting)}</div>
          )}
        </div>
      );
    case Kind.ExtraHours:
      return "";
    case Kind.FreeText:
      return "";
    case Kind.Lighting:
      return "";
    case Kind.PassiveArea:
      // eslint-disable-next-line no-case-declarations
      const pa = data as PassiveArea;
      return `${pa.length} x ${pa.width} m`;
    case Kind.SupplyDuctAngular:
      // eslint-disable-next-line no-case-declarations
      const sda = data as SupplyDuctAngular;
      return `${sda.width} x ${sda.height} mm`;
    case Kind.SupplyDuctRound:
      // eslint-disable-next-line no-case-declarations
      const sdr = data as SupplyDuctRound;
      return `ø ${sdr.diameter} mm`;
    case Kind.VentilatedCeiling:
      // eslint-disable-next-line no-case-declarations
      const vc = data as VentilatedCeiling;
      return `${vc.length} x ${vc.width} m`;
    case Kind.VentilatedCeilingSystem:
      // eslint-disable-next-line no-case-declarations
      const vcs = data as VentilatedCeilingSystem;
      return (
        <div>
          {(vcs.ventilatedCeiling.data as VentilatedCeiling).length > 0 && (
            <div>{measures(vcs.ventilatedCeiling)}</div>
          )}
          {(vcs.aerosolSeparator.data as AerosolSeparator).count > 0 && (
            <div>{measures(vcs.aerosolSeparator)}</div>
          )}
          {(vcs.dummyPlate.data as DummyPlate).count > 0 && (
            <div>{measures(vcs.dummyPlate)}</div>
          )}
          {(vcs.lighting.data as Lighting).count > 0 && (
            <div>{measures(vcs.lighting)}</div>
          )}
        </div>
      );
    default:
      return "";
  }
}

function PhotoDocumentationButtons({
  workOrder,
  position,
  disabled,
}: {
  workOrder: WorkOrder;
  position: Position;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center space-x-2">
      <PhotoDocumentation
        workOrder={workOrder}
        position={position}
        beforeOrAfter="before"
        disabled={disabled}
      />
      <PhotoDocumentation
        workOrder={workOrder}
        position={position}
        beforeOrAfter="after"
        disabled={disabled}
      />
    </div>
  );
}

function PhotoDocumentation({
  workOrder,
  position,
  beforeOrAfter,
  disabled,
}: {
  workOrder: WorkOrder;
  position: Position;
  beforeOrAfter: "before" | "after";
  disabled?: boolean;
}) {
  const { data: documentation } = useVbsDocumentationShowQuery({
    id: workOrder.documentationId,
  });

  const documentationItem = documentation?.items.find(
    (item) => item.positionId === position.id,
  );

  if (positionHasNoFoto(documentationItem, beforeOrAfter)) {
    return (
      <UploadImageButton
        workOrder={workOrder}
        position={position}
        beforeOrAfter={beforeOrAfter}
        disabled={disabled}
      >
        <CircleAlert className="text-destructive mr-2 h-4 w-4" />
        <span className="mr-1">
          {t("Foto")} {t(beforeOrAfter)}
        </span>
      </UploadImageButton>
    );
  }

  return (
    <ImageEditDialog
      documentationItem={documentationItem!}
      workOrder={workOrder}
      position={position}
      beforeOrAfter={beforeOrAfter}
      disabled={disabled}
    />
  );
}

function positionHasNoFoto(
  documentationItem: Item | undefined,
  beforeOrAfter: "before" | "after",
) {
  return (
    !documentationItem ||
    (beforeOrAfter === "before" &&
      documentationItem.beforeImages.length === 0) ||
    (beforeOrAfter === "after" && documentationItem.afterImages.length === 0)
  );
}

function UploadImageButton({
  beforeOrAfter,
  workOrder,
  position,
  children,
  disabled,
}: {
  workOrder: WorkOrder;
  position: Position;
  beforeOrAfter: "before" | "after";
  children: ReactNode;
  disabled?: boolean;
}) {
  const [upload, { isLoading, error, isSuccess, reset }] =
    useVbsDocumentationUploadImageForPositionMutation();
  const { toast } = useToast();

  useEffect(() => {
    if (isSuccess) {
      toast({
        title: t("Bild hochgeladen"),
        description: t("Das Bild wurde erfolgreich hochgeladen."),
        variant: "success",
      });
      reset();
    }
  }, [isSuccess, reset, toast]);

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
        variant: "destructive",
      });
      reset();
    }
  }, [error, reset, toast]);

  const onInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      // compress image
      const compressedFile = await imageCompression(e.target.files[0], {
        maxSizeMB: 0.2,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });

      // upload compressed image
      upload({
        id: workOrder.documentationId,
        positionId: position.id,
        positionNumber: position.number,
        componentKind: position.component.kind,
        beforeOrAfter,
        imageId: v4(),
        image: compressedFile,
      });

      // reset input field
      e.target.value = "";
      e.target.files = null;
    }
  };

  return (
    <Label
      className={cn(
        "ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center rounded-md text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
        "border-input bg-background hover:bg-accent hover:text-accent-foreground border",
        "h-9 rounded-md px-3",
      )}
    >
      <Input
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={onInputChange}
        disabled={disabled || isLoading}
      />
      {isLoading && (
        <span className="flex items-center">
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          <span>{t("Hochladen...")}</span>
        </span>
      )}
      {!isLoading && children}
    </Label>
  );
}

function ImageEditDialog({
  documentationItem,
  workOrder,
  position,
  beforeOrAfter,
  disabled,
}: {
  documentationItem: Item;
  workOrder: WorkOrder;
  position: Position;
  beforeOrAfter: "before" | "after";
  disabled?: boolean;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild disabled={disabled}>
        <Button variant="outline" size="sm">
          <Check className="text-green-700" />
          <span className="mr-1">
            {t("Foto")} {t(beforeOrAfter)}
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("Fotos")} {t(beforeOrAfter)}
          </DialogTitle>
          <DialogDescription>{t("Fotos für diese Position")}</DialogDescription>
        </DialogHeader>
        <PhotoCarousel
          documentationId={workOrder.documentationId}
          documentationItem={documentationItem}
          beforeOrAfter={beforeOrAfter}
        />
        <UploadImageButton
          workOrder={workOrder}
          position={position}
          beforeOrAfter={beforeOrAfter}
          disabled={disabled}
        >
          {t("Zusätzliches Foto hochladen")}
        </UploadImageButton>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoCarousel({
  documentationId,
  documentationItem,
  beforeOrAfter,
}: {
  documentationId: string;
  documentationItem: Item;
  beforeOrAfter: "before" | "after";
}) {
  const images =
    beforeOrAfter === "before"
      ? documentationItem.beforeImages
      : documentationItem.afterImages;

  return (
    <Carousel>
      <CarouselContent>
        {images.map((image) => (
          <PhotoCarouselItem
            key={image.id}
            documentationId={documentationId}
            itemId={documentationItem.id}
            image={image}
          />
        ))}
      </CarouselContent>
      <CarouselPrevious className="left-6" />
      <CarouselNext className="right-6" />
    </Carousel>
  );
}

function PhotoCarouselItem({
  documentationId,
  itemId,
  image,
}: {
  documentationId: string;
  itemId: string;
  image: Image;
}) {
  const {
    data: imageData,
    isLoading,
    error,
  } = useVbsDocumentationShowImageQuery({
    id: documentationId,
    imageId: image.id,
  });

  return (
    <CarouselItem>
      <div className="p-1">
        <Card>
          <CardContent className="relative flex min-h-96 items-center justify-center p-0">
            {isLoading && <Spinner />}
            {error && <RTKQueryErrorAlert error={error} />}
            {imageData && (
              <img className="max-h-96" src={imageData!.objectURL} alt="" />
            )}
            <DeleteImageButton
              documentationId={documentationId}
              itemId={itemId}
              imageId={image.id}
            />
          </CardContent>
        </Card>
      </div>
    </CarouselItem>
  );
}

function DeleteImageButton(props: {
  documentationId: string;
  itemId: string;
  imageId: string;
}) {
  const { documentationId, itemId, imageId } = props;
  const [open, setOpen] = useState(false);
  const [deleteImage, { isLoading, error, isSuccess, reset }] =
    useVbsDocumentationDeleteImageMutation();

  const onClick = () => {
    if (isLoading) {
      return;
    }
    deleteImage({
      id: documentationId,
      itemId,
      imageId,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      reset();
      setOpen(false);
    }
  }, [isSuccess, setOpen, reset]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="absolute top-4 right-4" variant="destructive">
          <Trash className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>{t("Bild wirklich löschen?")}</DialogTitle>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <Button onClick={onClick} variant="destructive">
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
